// src/pages/Blog.js
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../App.css";

/* ── Blog data ───────────────────────────────────────── */
const stories = [
  {
    id: 1,
    emoji: "🌟",
    title: "She Saw More Than Just the Struggles",
    subtitle: "How One Teacher Changed the Life of a Twice-Exceptional Learner",
    image: "/images/blog1.jpg",
    excerpt:
      "Discover how understanding neurodiversity transformed a frustrated student into a confident innovator.",
    body: `Jayden didn’t fit the mold.
He was nine, wildly gifted in math, able to solve equations that made adults pause. But every day in school was a battle. Not because of the content. Because of the expectations.

He couldn’t sit still. He’d interrupt, lose focus, bounce his leg for hours. Teachers said he was “too much,” “too distracted,” or “just not trying hard enough.” But he was trying every single day. Trying to exist in a world that didn’t know how to meet a gifted brain wrapped in ADHD.

Then came Ms. Leena.

She didn’t see Jayden as a disruption. She saw him as a thinker.
She moved his desk near a window. Gave him a fidget. Introduced “math leadership moments” where Jayden could help solve tricky puzzles on the board. She didn’t punish movement, she planned for it.

One day, she told him, “You don’t have to shrink to fit this room. Let’s stretch the room to fit you.”

For the first time, Jayden didn’t feel broken. He felt brilliant.

Now he wants to be a mathematician who helps design classrooms for kids like him—because one teacher didn’t try to fix him. She chose to understand him.`,
  },
  {
    id: 2,
    emoji: "🎨",
    title: "From Meltdowns to Masterpieces",
    subtitle: "Celebrating Neurodiverse Talents Through Art and Story",
    image: "/images/blog2.jpg",
    excerpt:
      "Voices from the movement: stories, drawings, and poems from students who just needed to be seen.",
    body: `Maya spoke in colors.
She didn’t speak much with words, not in the way the classroom expected. On most days, she kept to herself, her voice hidden beneath the weight of sensory overload and social anxiety.

Other students called her shy. Some called her strange. But in the back of her notebook? Maya was telling stories no one had ever seen—vivid, detailed drawings of fantasy worlds, soft moments, silent heroes who felt… just like her.

That’s when Ms. Adu noticed.

Instead of asking Maya to speak up, she invited Maya to share her art in a new way. The class began “Story Through Sketch,” a project where everyone wrote short tales inspired by Maya’s drawings. Slowly, Maya began sharing her own titles. Then little captions. Then full sentences. And one morning, with her heart pounding and her artwork glowing on the projector, Maya read aloud:
“This is my world. And I want you in it.”

Today, Maya writes illustrated stories about girls who build dream machines and save gentle monsters. She says they’re based on people she knows. Like Ms. Adu.`,
  },
  {
    id: 3,
    emoji: "🌈",
    title: "The Calm Corner That Saved the Day",
    subtitle: "Creating Sensory-Friendly Classrooms, One Space at a Time",
    image: "/images/blog3.jpg",
    excerpt:
      "Practical ways educators adapt learning spaces for emotional and sensory regulation.",
    body: `Aliyah was a quiet storm.
On the surface, she was withdrawn. But inside, everything was loud: fluorescent lights, ticking clocks, screeching chairs. Her world didn’t feel safe. It felt loud.

She’d try her best until it was too much. Then came shutdowns, tears, blank stares. Most teachers saw behavior. Mr. Joseph saw a signal.

He introduced something simple, yet revolutionary: a calm corner.
Soft lights. Weighted cushions. Noise-canceling headphones. A “take-a-break” pass students could use without asking.

For the first time, Aliyah had a space that honored her needs without shame.

Bit by bit, she re-entered the classroom on her own terms, raising her hand, helping a peer, smiling. The calm corner wasn’t a reward. It was a right. It was belonging made visible.

Aliyah says her classroom feels like home now.
And Mr. Joseph? He says, “Every child should know: You are not wrong for feeling differently. There is room for you here.”`,
  },
];

/* ── Simple fade variants ───────────────────────────── */
const fade = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.4 } },
};

const Blog = () => {
  const [active, setActive] = useState(null); // null ==> show grid

  return (
    <div className="container" style={{ padding: "2rem 1rem" }}>
      <AnimatePresence mode="wait">
        {/* ---------- GRID MODE ---------- */}
        {active === null && (
          <motion.div key="grid" initial="hidden" animate="visible" exit="hidden" variants={fade}>
            <h1>📰 From the Blog</h1>
            <p style={{ color: "#4b5563", maxWidth: "720px" }}>
              Insights, experiences, and powerful stories that shape the heart of inclusive
              education. Learn from educators, students, parents, and advocates around the world.
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))",
                gap: "1.5rem",
                marginTop: "2rem",
              }}
            >
              {stories.map((post, idx) => (
                <motion.div
                  key={post.id}
                  className="card"
                  initial={{ opacity: 0, y: 25 }}
                  animate={{ opacity: 1, y: 0, transition: { delay: idx * 0.1 } }}
                >
                  <img
                    src={post.image}
                    alt={post.title}
                    style={{
                      width: "100%",
                      height: "180px",
                      objectFit: "cover",
                      borderRadius: "12px",
                    }}
                  />
                  <h2 style={{ marginTop: "1rem", fontSize: "1.2rem" }}>
                    {post.emoji} {post.title}
                  </h2>
                  <p style={{ color: "#4b5563" }}>{post.excerpt}</p>
                  <button className="btn" onClick={() => setActive(post)} style={{ marginTop: "1rem" }}>
                    Read More
                  </button>
                </motion.div>
              ))}
            </div>

            {/* Contribute CTA */}
            <div className="card" style={{ textAlign: "center", marginTop: "4rem" }}>
              <h2>📬 Share Your Voice</h2>
              <p>
                Do you have a classroom story, parenting insight, or creative idea to share? We
                welcome articles, videos, and stories from the community.
              </p>
              <a href="mailto:info@mydearteachermovement.com" className="btn">
                Contribute a Story
              </a>
            </div>
          </motion.div>
        )}

        {/* ---------- SINGLE POST MODE ---------- */}
        {active && (
          <motion.article
            key="single"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={fade}
            style={{ maxWidth: 800, margin: "0 auto" }}
          >
            <button onClick={() => setActive(null)} className="btn grey-btn" style={{ marginBottom: "1rem" }}>
              ← Back to Blog
            </button>

            <h1>
              {active.emoji} {active.title}
            </h1>
            <h3 style={{ fontStyle: "italic", color: "#666", marginBottom: "1.5rem" }}>
              {active.subtitle}
            </h3>

            <p style={{ whiteSpace: "pre-line", lineHeight: 1.8, color: "#444" }}>{active.body}</p>

            <button onClick={() => setActive(null)} className="btn grey-btn" style={{ marginTop: "2rem" }}>
              ← Back to Blog
            </button>
          </motion.article>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Blog;
